/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Component } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

import { Button, Dialog } from 'primitives';
import { MultiSelectInput, PermissionSelector, PlanLimitWarning } from 'components';
import { invitationModalStore, licenseStore, organizationStore, projectStore } from 'stores';
import { DEV_LICENSE_MAX_INPUT_EMAILS, MAX_INPUT_EMAILS } from 'utils/constants';
import debounce from 'utils/debounce';
import { invitationService } from 'services';
import { AllMembersSuggestionOption } from 'features/bulk-project-invite';

import * as Styled from './InvitationModal.styled';

const MAX_MESSAGE_LENGTH = 500;

export class InvitationModal extends Component {
  static propTypes = {
    onClose: PropTypes.func
  };

  componentDidMount() {
    const { project, collaborators } = projectStore;
    invitationModalStore.setProject(project, collaborators);
  }

  componentWillUnmount() {
    invitationModalStore.reset();
  }

  handleClick = async (currentOrganizationId, externalInvitees) => {
    await invitationModalStore.handleInviteClick(currentOrganizationId, externalInvitees);
    await projectStore.fetchCollaboratorData(projectStore.project.id);
  };

  handleInviteAllOrganizationMembers = async () => {
    const organizationMembers = await invitationService.fetchInvitationSuggestions({
      // @ts-expect-error TS2353
      searchQuery: null
    });

    await invitationModalStore.handleInviteAll(organizationMembers, 4);
    await projectStore.fetchCollaboratorData(projectStore.project.id);
  };

  handleOnChange = debounce((value) => {
    if (value?.length < 2) {
      return;
    }

    invitationModalStore.fetchInviteSuggestions({ searchQuery: value });
  }, 250);

  render() {
    const { project } = projectStore;

    const { permission, message, invitees, suggestions, isSendingInvitations, collaborators } =
      invitationModalStore.state;

    const { handleMessageChange, handlePermissionChange, messageLength, buttonDisabled, buttonText, validate } =
      invitationModalStore;
    const { currentOrganizationInfo, currentOrganizationId, organizationManagementPageUrl } = organizationStore;

    const canAddAnyUser = organizationStore.hasGeneralUserCreatePermission;

    const externalInvitees = invitees.filter((email) => !suggestions.find((suggestion) => suggestion.email === email));

    const { isDeveloperLicense } = licenseStore;

    let maxInputEmailsCount = MAX_INPUT_EMAILS;
    let availableInvites = MAX_INPUT_EMAILS;
    let maximumCollaboratorsReached = false;
    if (isDeveloperLicense) {
      maxInputEmailsCount = DEV_LICENSE_MAX_INPUT_EMAILS;
      availableInvites = DEV_LICENSE_MAX_INPUT_EMAILS - (collaborators.length - 1) - invitees.length;
      maximumCollaboratorsReached = availableInvites <= 0;
    }

    const planLimitWarning = maximumCollaboratorsReached ? <PlanLimitWarning /> : null;

    return (
      // @ts-expect-error TS2739
      <Dialog open={invitationModalStore.state.isInvitationModalVisible} onClose={invitationModalStore.hide}>
        <Dialog.Header>
          <Dialog.Title data-test="invitation-modal-title">Invite collaborators to "{project.name}"</Dialog.Title>
          <Dialog.Subtitle>Invited users will gain access to all files in the project.</Dialog.Subtitle>
        </Dialog.Header>

        <Dialog.Content>
          <MultiSelectInput
            placeholder={
              isDeveloperLicense
                ? maximumCollaboratorsReached
                  ? `You have reached the maximum number of collaborators for your license.`
                  : `Write or paste up to ${availableInvites} names or email addresses and press Enter or Tab`
                : 'Search for name or email...'
            }
            onChange={this.handleOnChange}
            onValidate={(email) => validate(email, { validateOrganizationInvitee: !canAddAnyUser })}
            dataSource={invitees}
            suggestions={suggestions}
            maxLength={maximumCollaboratorsReached ? 1 : maxInputEmailsCount}
            action={<PermissionSelector value={permission} onChange={handlePermissionChange} />}
            organizationMembersOnly={!canAddAnyUser}
            warningComponent={planLimitWarning}
            showAllMembersSuggestion={!isSendingInvitations}
            disabled={isSendingInvitations}
            primarySuggestion={<AllMembersSuggestionOption handleOnClick={this.handleInviteAllOrganizationMembers} />}
          />

          <Styled.Textarea
            // @ts-expect-error TS2769
            placeholder={`You have been invited to ${project.name}...`}
            multiline
            rows={5}
            value={message}
            onChange={handleMessageChange}
            disabled={isSendingInvitations}
            data-test="invitation-message"
          />

          <Styled.HelperText data-test="character-count">
            {messageLength > MAX_MESSAGE_LENGTH
              ? 'Too many characters.'
              : `${MAX_MESSAGE_LENGTH - messageLength} characters left.`}
          </Styled.HelperText>

          {currentOrganizationInfo && externalInvitees?.length > 0 && (
            <Styled.ExternalUserWarning>
              The following {externalInvitees.length > 1 ? 'users' : 'user'} will also be invited to the{' '}
              <strong>{currentOrganizationInfo.name}</strong> organization with <strong>read/write</strong> access to
              all applications:{' '}
              {externalInvitees.map((email, index) => (
                <span key={email}>
                  <strong>{email}</strong>
                  {index < externalInvitees.length - 1 && ', '}
                </span>
              ))}
              .
              <br />
              You can modify their permissions later through{' '}
              <a href={`${organizationManagementPageUrl}/users`} target="_blank" rel="noreferrer">
                Cloud User Management.
              </a>
            </Styled.ExternalUserWarning>
          )}
        </Dialog.Content>

        <Dialog.Footer>
          {/* @ts-expect-error TS2322 */}
          <Button
            disabled={buttonDisabled}
            onClick={() => this.handleClick(currentOrganizationId, externalInvitees)}
            variant="primary"
            data-test="invite-email-button"
          >
            {/* @ts-expect-error TS2769 */}
            {isSendingInvitations && <Styled.LoadingAnimation size="small" $light />}
            {buttonText}
          </Button>
        </Dialog.Footer>
      </Dialog>
    );
  }
}

export default observer(InvitationModal);
